import { Component, ElementRef, HostListener, Output, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HelpTicket } from '@shared/models/help-ticket.model';
import { ICatalog } from '@shared/models/icommons.model';
import { IUser } from '@shared/models/iuser.model';
import { DataService } from '@shared/services/data.service';
import { SharedService } from '@shared/services/shared.service';
import { MenuItem } from 'primeng/api';
import { take } from 'rxjs/operators';

@Component({
  selector: 'miq-ticket-menu',
  templateUrl: './ticket-menu.component.html',
  styleUrls: ['./ticket-menu.component.scss'],
  standalone: false,
})
export class TicketMenuComponent implements OnInit, OnDestroy {
  public menuItems: MenuItem[] = [
    {
      label: 'CHANGE STATUS',
      title: 'shared.changeStatus',
      command: this.toggleStatus.bind(this),
    },
    { label: 'GROUP', title: 'shared.group', command: this.group.bind(this) },
    {
      label: 'UNGROUP',
      title: 'shared.ungroup',
      command: this.ungroup.bind(this),
    },
    {
      label: 'MOVE TOP TOP',
      title: 'shared.moveToTop',
      command: this.toTop.bind(this),
    },
    {
      label: 'MOVE TO BOTTOM',
      title: 'shared.moveToBottom',
      command: this.toBottom.bind(this),
    },
  ];
  @Output() public ticketStatuses: ICatalog[];
  public objectKeys = Object.keys;

  private currentGroup: HelpTicket[];
  private currentCheckedTickets: any = {};
  private userInfo: IUser;
  public showStatus = false;
  public showMenu: boolean;

  constructor(
    private shared: SharedService,
    private elementRef: ElementRef,
    private data: DataService,
    private activatedRoute: ActivatedRoute
  ) {
    this.activatedRoute.data.subscribe(routeData => (this.userInfo = routeData.userInfo));
    this.shared
      .getServiceTicketStatuses()
      .pipe(take(1))
      .subscribe(result => {
        this.ticketStatuses = result;
      });
  }

  ngOnInit() {
    /* required for static label translation */
  }

  public open(ticketGroup: HelpTicket[], event: MouseEvent, checkedTickets: any, showMenu: boolean) {
    this.showMenu = showMenu;
    this.currentGroup = ticketGroup;
    this.currentCheckedTickets = checkedTickets || {};
    const left = event.x - 40;
    const top = event.y - 180;
    this.elementRef.nativeElement.style.top = `${top}px`;
    this.elementRef.nativeElement.style.left = `${left}px`;
    this.elementRef.nativeElement.style.display = 'block';
  }

  public close() {
    this.elementRef.nativeElement.style.display = 'none';
  }

  public toggleStatus(): void {
    this.showStatus = !this.showStatus;
  }

  public isExistingStatus(ticketStatus): boolean {
    return this.currentGroup[0].status === ticketStatus.value;
  }

  private group() {
    if (Object.keys(this.currentCheckedTickets).length > 1) {
      return this.shared
        .postGroupTickets(this.fromCheckedToArray(this.currentCheckedTickets))
        .pipe(take(1))
        .subscribe(() => this.data.setMenuActionCompleted());
    }
  }

  public ungroup() {
    let ticketsToUngroup: HelpTicket[][];
    if (Object.keys(this.currentCheckedTickets).length > 0) {
      ticketsToUngroup = this.fromCheckedToArray(this.currentCheckedTickets);
    } else if (this.currentGroup.length > 1) {
      ticketsToUngroup = [this.currentGroup];
    }
    return this.shared
      .postUngroupTickets(ticketsToUngroup)
      .pipe(take(1))
      .subscribe(() => this.data.setMenuActionCompleted());
  }

  private toTop() {
    return this.shared
      .putTicketToTop(this.getTicketsIds())
      .pipe(take(1))
      .subscribe(() => this.data.setMenuActionCompleted());
  }

  private toBottom() {
    return this.shared
      .putTicketToBottom(this.getTicketsIds())
      .pipe(take(1))
      .subscribe(() => this.data.setMenuActionCompleted());
  }

  private getTicketsIds() {
    return Object.keys(this.currentCheckedTickets).length > 0
      ? this.fromCheckedToArray(this.currentCheckedTickets).map(group => group[0].id)
      : [this.currentGroup[0].id];
  }

  private fromCheckedToArray(checkedTickets: any): HelpTicket[][] {
    return Object.keys(checkedTickets).reduce((acc, key) => [...acc, checkedTickets[key]], []);
  }

  public changeStatus(statusKey: number): void {
    const ticketStatus = {
      ticketIds: this.getTicketsIds(),
      statusId: statusKey,
      updateBy: `${this.userInfo.firstName} ${this.userInfo.lastName}`,
    };
    this.shared
      .changeTicketStatus(ticketStatus)
      .pipe(take(1))
      .subscribe(() => this.data.setMenuActionCompleted());
  }

  @HostListener('document:click', ['$event'])
  private clickElseWhere(event: MouseEvent) {
    const { target } = event;
    if (
      target instanceof HTMLElement &&
      !target.className.includes('options') &&
      !target.title.includes('CHANGE STATUS')
    ) {
      this.close();
      this.showStatus = false;
      this.showMenu = false;
    }
  }

  ngOnDestroy() {
    /* required for static label translation */
  }
}
